import Badge from "../../../../ui/Badge/Badge";
import styles from "./ArticleDetails.module.scss";
import {
  FragmentType,
  getFragmentData,
  graphql,
} from "../../../../__generated__/gql";
import { useCallback, useEffect, useState } from "react";
import Tooltip from "../../../../ui/Tooltip/Tooltip";
import { Check, Help } from "../../../../ui/Icon/Icon";
import Stats from "./Stats/Stats";
import Details from "./Details/Details";
import articleContentSchema from "./ArticleContent/schema";
import { z } from "zod";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import Button from "../../../../ui/Button/Button";
import classNames from "classnames";
import { useMutation } from "urql";
import { useToast } from "../../../../ui/Toast/ToastContext";
import { ArticleStatus } from "../../../../__generated__/gql/graphql";
import ArticleContent from "./ArticleContent/ArticleContent";

const UpdateArticleDetailsMutation = graphql(/* GraphQL */ `
  mutation updateArticleDetails($input: UpdateArticleInput!) {
    updateArticle(input: $input) {
      ...ArticleDetails
    }
  }
`);

const ArticleDetailsFragment = graphql(/* GraphQL */ `
  fragment ArticleDetails on Article {
    ...ArticleDetailsDetails
    ...ArticleDetailsStats
    stats {
      contentGrade
    }
    body
    status
    id
    title
    keyword
    tag {
      id
      name
    }
    metaDescription
    excerpt
    additionalPrompt
  }
`);

type ArticleDetailsFormData = z.infer<typeof articleContentSchema>;

function ArticleDetails({
  article: articleFragment,
  saving,
  onChange,
}: {
  article: FragmentType<typeof ArticleDetailsFragment>;
  saving: boolean;
  onChange?: (isDirty: boolean) => void;
}) {
  const { showToast } = useToast();
  const [footerRef, setFooterRef] = useState<HTMLDivElement | null>(null);
  const [, updateArticleDetails] = useMutation(UpdateArticleDetailsMutation);
  const article = getFragmentData(ArticleDetailsFragment, articleFragment);
  const [isOpen, setIsOpen] = useState<string | null>("Details");
  const {
    control,
    handleSubmit,
    reset,
    formState: { isDirty, isSubmitting },
  } = useForm<ArticleDetailsFormData>({
    resolver: zodResolver(articleContentSchema),
    defaultValues: {
      keyword: article.keyword,
      title: article.title ?? "",
      tag: article.tag?.id,
      metaDescription: article.metaDescription ?? "",
      additionalPrompt: article.additionalPrompt ?? "",
    },
  });

  const toggleIsOpen = useCallback(
    (value: string) =>
      setIsOpen((previous) => (previous === value ? null : value)),
    [],
  );

  const onSubmit = useCallback(
    async (data: ArticleDetailsFormData) => {
      await updateArticleDetails({
        input: {
          id: article.id,
          title: data.title,
          keyword: data.keyword,
          tag: data.tag,
          metaDescription: data.metaDescription,
          additionalPrompt: data.additionalPrompt,
        },
      });

      showToast({
        title: "Article updated",
        variant: "success",
      });

      reset(data);
    },
    [article.id, reset, showToast, updateArticleDetails],
  );

  useEffect(() => {
    onChange?.(isDirty);
  }, [isDirty, onChange]);

  useEffect(() => {
    reset((values) => ({
      ...values,
      title: article.title ?? "",
      keyword: article.keyword,
      tag: article.tag?.id,
      metaDescription: article.metaDescription ?? "",
      additionalPrompt: article.additionalPrompt ?? "",
    }));
  }, [
    article.additionalPrompt,
    article.keyword,
    article.metaDescription,
    article.status,
    article.tag?.id,
    article.title,
    reset,
  ]);

  return (
    <>
      <form className={styles.articleDetails} onSubmit={handleSubmit(onSubmit)}>
        {article.body && article.stats?.contentGrade && (
          <div className={styles.contentGrade}>
            <span className={styles.contentGradeLabel}>
              <span>Content Grade</span>
              <Tooltip
                asChild={false}
                content="The Macaw Content Grade summarizes the overall readability and usefulness of the article, as well as the likelihood of this article ranking on search engines."
              >
                <Help className={styles.contentGradeIcon} />
              </Tooltip>
            </span>
            <Badge color="success" showDot={false} size="medium" variant="pill">
              {article.stats.contentGrade}
            </Badge>
          </div>
        )}

        <Details
          data={article}
          isOpen={isOpen === "Details"}
          setIsOpen={toggleIsOpen}
        />
        <Stats
          data={article}
          isOpen={isOpen === "Stats"}
          setIsOpen={toggleIsOpen}
        />
        <ArticleContent
          title={article.title ?? ""}
          excerpt={article.excerpt ?? ""}
          status={article.status}
          control={control}
          isOpen={isOpen === "Article Content"}
          setIsOpen={toggleIsOpen}
          footer={footerRef}
          processing={article.status === ArticleStatus.Processing}
        />
        {isDirty && (
          <div className={classNames(styles.footer)} ref={setFooterRef}>
            <Button
              variant="outline"
              size="small"
              onClick={() => reset()}
              disabled={isSubmitting}
            >
              Cancel
            </Button>
            <Button
              variant="primary"
              type="submit"
              size="small"
              loading={isSubmitting}
            >
              Save
            </Button>
          </div>
        )}
      </form>
      <Badge
        className={styles.saveBadge}
        color="default"
        showDot={false}
        size="medium"
        variant="badge"
        loading={saving}
      >
        {saving ? (
          "Saving"
        ) : (
          <span className={styles.saved}>
            <Check size="xsmall" /> Saved
          </span>
        )}
      </Badge>
    </>
  );
}

export default ArticleDetails;
